<template>
  <div class="support-form">
    <FormIntroductionText
      v-if="props.model?.introductionVisible"
      :key="componentKey"
      class="support-introduction"
    />

    <form>
      <div class="name-section">
        <!-- First name -->
        <FormField
          id="firstName"
          :show-error="(touched.firstName || startValidation) && !!errors.firstName"
          :error-message="tt(errors.firstName)"
          :key="componentKey"
        >
          <FormInput
            id="firstName"
            data-cy="firstName"
            :placeholder="t('supportRequest.fields.firstName.fieldName')"
            v-model="formData.firstName"
            :is-disabled="isSubmitting || isUploading"
            :show-error="(touched.firstName || startValidation) && !!errors.firstName"
            @blur="(markTouched('firstName'), validateField('firstName'))"
          />
        </FormField>

        <!-- Last name -->
        <FormField
          id="lastName"
          :show-error="(touched.lastName || startValidation) && !!errors.lastName"
          :error-message="tt(errors.lastName)"
          :key="componentKey"
        >
          <FormInput
            id="lastName"
            data-cy="lastName"
            :placeholder="t('supportRequest.fields.lastName.fieldName')"
            v-model="formData.lastName"
            :is-disabled="isSubmitting || isUploading"
            :show-error="(touched.lastName || startValidation) && !!errors.lastName"
            @blur="(markTouched('lastName'), validateField('lastName'))"
          />
        </FormField>
      </div>

      <!-- Email -->
      <FormField
        id="email"
        :show-error="(touched.email || startValidation) && !!errors.email"
        :error-message="tt(errors.email)"
        :key="componentKey"
      >
        <FormInput
          id="email"
          data-cy="email"
          type="email"
          :placeholder="t('supportRequest.fields.email.fieldName')"
          v-model="formData.email"
          :is-disabled="isSubmitting || isUploading || isLoginRequired"
          :show-error="(touched.email || startValidation) && !!errors.email"
          @blur="(markTouched('email'), validateField('email'))"
        />
      </FormField>

      <!-- Phone -->
      <FormField
        id="phoneNumber"
        :show-error="(touched.phoneNumber || startValidation) && !!errors.phoneNumber"
        :error-message="tt(errors.phoneNumber)"
        :key="componentKey"
      >
        <FormInput
          id="phoneNumber"
          data-cy="phoneNumber"
          :placeholder="t('supportRequest.fields.phoneNumber.fieldName')"
          v-model="formData.phoneNumber"
          :show-error="(touched.phoneNumber || startValidation) && !!errors.phoneNumber"
          :is-disabled="isSubmitting || isUploading"
          inputmode="tel"
          @blur="(markTouched('phoneNumber'), validateField('phoneNumber'))"
        />
      </FormField>

      <!-- Subject -->
      <FormField
        id="subject"
        :show-error="(touched.subject || startValidation) && !!errors.subject"
        :error-message="tt(errors.subject)"
        :key="componentKey"
      >
        <FormInput
          id="subject"
          data-cy="subject"
          :placeholder="t('supportRequest.fields.subject.fieldName')"
          v-model="formData.subject"
          :show-error="(touched.subject || startValidation) && !!errors.subject"
          :is-disabled="isSubmitting || isUploading"
          @blur="(markTouched('subject'), validateField('subject'))"
        />
      </FormField>

      <!-- Error message -->
      <FormField
        id="errorMessage"
        :show-error="(touched.errorMessage || startValidation) && !!errors.errorMessage"
        :error-message="tt(errors.errorMessage)"
        :key="componentKey"
      >
        <FormInput
          id="errorMessage"
          data-cy="errorMessage"
          :placeholder="t('supportRequest.fields.errorMessage.fieldName')"
          v-model="formData.errorMessage"
          :show-error="(touched.errorMessage || startValidation) && !!errors.errorMessage"
          :is-disabled="isSubmitting || isUploading"
          @blur="(markTouched('errorMessage'), validateField('errorMessage'))"
        />
      </FormField>

      <!-- Priority -->
      <FormField
        id="priority"
        v-if="appStore.configuration?.components.supportRequest.prioritySelect.visible"
        :show-error="(touched.priority || startValidation) && !!errors.priority"
        :error-message="tt(errors.priority)"
        :key="componentKey"
      >
        <FormSelect
          id="priority"
          :is-disabled="isSubmitting || isUploading"
          :show-error="(touched.priority || startValidation) && !!errors.priority"
          :options="
            appStore.configuration?.components.supportRequest.prioritySelect
              .availablePriorities ?? [t('supportRequest.fields.priority.noPriorities')]
          "
          v-model="formData.priority"
          @update:modelValue="(markTouched('priority'), validateField('priority'))"
        />
      </FormField>

      <div class="product-section">
        <!-- Product -->
        <FormField
          id="product"
          v-if="appStore.configuration?.components.productSelect.visible"
          :key="componentKey"
        >
          <FormSelect
            id="product"
            :is-disabled="isSubmitting || isUploading"
            :options="
              appStore.configuration?.components.productSelect.availableProducts ?? [
                t('supportRequest.fields.product.noProducts'),
              ]
            "
            v-model="formData.product"
          />
        </FormField>

        <!-- Version -->
        <FormField
          id="version"
          :show-error="(touched.version || startValidation) && !!errors.version"
          :error-message="tt(errors.version)"
          :key="componentKey"
        >
          <FormInput
            id="version"
            data-cy="version"
            :placeholder="t('supportRequest.fields.version.fieldName')"
            v-model="formData.version"
            :is-disabled="isSubmitting || isUploading"
            :show-error="(touched.version || startValidation) && !!errors.version"
            @blur="(markTouched('version'), validateField('version'))"
          />
        </FormField>
      </div>

      <div class="description-section">
        <!-- Description -->
        <FormField
          id="description"
          :show-error="(touched.description || startValidation) && !!errors.description"
          :error-message="tt(errors.description)"
          :key="componentKey"
        >
          <FormInput
            id="description"
            data-cy="description"
            :placeholder="t('supportRequest.fields.description.fieldName')"
            v-model="formData.description"
            multiline
            :is-disabled="isSubmitting || isUploading"
            :show-error="(touched.description || startValidation) && !!errors.description"
            @blur="(markTouched('description'), validateField('description'))"
          />
        </FormField>
      </div>

      <!-- Attachments -->
      <FormField id="attachments" :key="componentKey" class="form-attachments">
        <FormAttachments
          id="attachments"
          :model="props.model?.attachmentsInput"
          v-model="formData.attachments"
          :is-disabled="isSubmitting || isUploading"
          :label="t('supportRequest.fields.attachment.buttonText')"
          :chatHistoryBytes="chatHistoryBytes"
        />
      </FormField>

      <div ref="captchaRef" style="display: inline-block" />
      <!-- Submit -->
      <div class="form-actions">
        <Button
          class="submit-button"
          :model="props.model?.supportRequestSendButton"
          :is-disabled="isSubmitting || isUploading"
          :alt="t('supportRequest.submitButton')"
          :text="
            !isUploading
              ? t('supportRequest.submitButton')
              : t('supportRequest.submitButtonSending')
          "
          @click="handleSubmit"
          :key="componentKey"
          data-cy="submit-button"
        >
        </Button>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref, watch } from 'vue'
import FormField from './FormField.vue'
import FormInput from './FormInput.vue'
import FormSelect from './FormSelect.vue'
import FormAttachments from './FormAttachments.vue'
import FormIntroductionText from './FormIntroductionText.vue'
import type { SupportRequest } from '../../types/configuration/SupportRequest'
import { updateComponentKey } from '../../composables/ComponentKey'
import { useValidation } from '../../composables/supportRequest/SupportFormValidation'
import { t, tt } from '../../composables/Translation'
import { useRecaptchaV2 } from '../../composables/UseRecaptchaV2'
import { useSupportForm } from '../../composables/supportRequest/SupportFormUpload'
import { createSupportFormSchema } from '../../composables/supportRequest/SupportFormRules'
import { useChatStore } from '../../stores/chatStore'
import { useAidaSessionStore } from '../../stores/aidaSessionStore'
import Button from '../Button.vue'
import { useUserProfileStore } from '../../stores/userProfileStore'
import { storeToRefs } from 'pinia'
import { useAppStore } from '../../stores/appStore'
import { useToast } from '../../composables/useToast'
import { getDefaultProduct } from '../../composables/ProductSelector'
import { exportChatText } from '../../composables/ExportChatText'
import { LoginRequired } from '../../types/app/loginRequired'

const chatStore = useChatStore()
const aidaSessionStore = useAidaSessionStore()
const userProfileStore = useUserProfileStore()
const appStore = useAppStore()

const isLoginRequired =
  appStore.configuration?.login.isLoginRequired === LoginRequired.Always ||
  appStore.configuration?.login.isLoginRequired === LoginRequired.SRRequiredOnly

const {
  containerEl: captchaRef,
  execute,
  reset: resetRecaptcha,
} = useRecaptchaV2({
  size: 'invisible',
  badge: 'bottomleft',
})

// No-op to mark captchaRef as "used" for TypeScript/ESLint
void captchaRef

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'submitStart'): void
  (e: 'submitEnd'): void
}>()

defineExpose<{ resetFormData: () => void }>({ resetFormData })

const toast = useToast()

const { loading: isUploading, submitSupportForm } = useSupportForm()

const props = defineProps<{ model: SupportRequest | undefined }>()

const { profile } = storeToRefs(userProfileStore)

const defaultPriority = appStore.configuration?.components.supportRequest.prioritySelect.visible
  ? appStore.configuration.components.supportRequest.prioritySelect.defaultPriority
  : ''

const formData = reactive({
  email: profile.value?.email || '',
  firstName: profile.value?.name || '',
  lastName: profile.value?.surname || '',
  phoneNumber: '',
  subject: '',
  description: '',
  priority: defaultPriority,
  product: getDefaultProduct(),
  version: '',
  errorMessage: '',
  attachments: [] as File[],
})

watch(profile, newProfile => {
  if (newProfile) {
    formData.email = newProfile.email
    formData.firstName = newProfile.name
    formData.lastName = newProfile.surname
  }
})

const schema = createSupportFormSchema(
  props.model,
  !!appStore.configuration?.components.supportRequest.prioritySelect.visible,
  appStore.configuration?.components.supportRequest.validationRules.version.required ?? false
)

const { errors, touched, validateField, validateAll, markTouched, resetValidation } = useValidation(
  formData,
  schema
)

function markAllTouched() {
  ;(Object.keys(schema) as (keyof typeof formData)[]).forEach(k => markTouched(k))
}

const startValidation = ref(false)
function registerValidation() {
  startValidation.value = true
}

function resetFormData() {
  Object.assign(formData, {
    email: profile.value?.email || '',
    firstName: profile.value?.name || '',
    lastName: profile.value?.surname || '',
    phoneNumber: '',
    subject: '',
    description: '',
    priority: defaultPriority,
    product: getDefaultProduct(),
    version: '',
    errorMessage: '',
    attachments: [] as File[],
  })

  startValidation.value = false
  resetValidation()
}

const isSubmitting = ref(false)
const chatHistoryBytes = ref(0)

async function handleSubmit() {
  if (isSubmitting.value || isUploading.value) return
  registerValidation()
  if (!validateAll()) {
    markAllTouched()
    return
  }
  isSubmitting.value = true
  emit('submitStart')
  console.log('Priority: ', formData.priority)
  try {
    // make sure the widget is in a clean state for a fresh run
    resetRecaptcha()
    const recaptchaToken = await execute()

    const files = formData.attachments

    const chatHistory = exportChatText().buildAlternatingMessages()

    chatHistoryBytes.value = new TextEncoder().encode(chatHistory).length

    const payload = {
      question_number: chatStore.questionNumber.toString(),
      user_session_id: aidaSessionStore.aidaSessionId || '',
      email: formData.email,
      first_name: formData.firstName,
      last_name: formData.lastName,
      phone_number: formData.phoneNumber,
      subject: formData.subject,
      description: formData.description,
      priority: formData.priority,
      product: formData.product,
      version: formData.version,
      error_message: formData.errorMessage,
      recaptcha_token: recaptchaToken,
      history: chatHistory,
      files,
    } as const

    await submitSupportForm(payload)
    toast.success(t('supportRequest.successfulSubmission'))
    startValidation.value = false
    resetFormData()
    appStore.supportRequestButtonVisible = false
    closeModal()
  } catch (e) {
    toast.error(`${t('supportRequest.failedSubmission')}`)
    console.error('Error: Failed to send SR', e)
  } finally {
    resetRecaptcha()
    isSubmitting.value = false
    emit('submitEnd')
  }
}

watch(
  formData,
  () => {
    if (startValidation.value) validateAll()
  },
  { deep: true }
)

let componentKey = updateComponentKey(props)

function closeModal() {
  emit('close')
}
</script>

<style lang="scss" scoped>
.name-section {
  padding: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.product-section {
  padding: 0;
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 16px;
}

.submit-button {
  align-self: flex-end;
  padding: 8px 16px;
  border-radius: 8px;
  font: inherit;
  border: 1px solid #e3e8f1;
  background: #f4f6fa;
  color: #6b7280;
  font-size: 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition:
    background-color 120ms ease,
    transform 120ms ease,
    box-shadow 120ms ease;
  box-shadow:
    0 1px 0 rgba(0, 0, 0, 0.03),
    inset 0 -1px 0 rgba(255, 255, 255, 0.5);
}

.form-attachments {
  padding: 0px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
}
</style>
